import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, PartyPopper } from "lucide-react";
import { PageHeader } from "../components/site/PageHeader";
import { PhotoPlaceholder } from "../components/site/PhotoPlaceholder";
import { Reveal } from "../components/site/Reveal";
import { Field } from "../components/site/ContactForm";
import { API } from "../lib/api";
import { restaurant, eventTypes } from "../data/siteData";

const empty = { name: "", email: "", phone: "", event_type: eventTypes[0], event_date: "", guests: "", message: "", consent: false, website: "" };

const HallForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Merci d’accepter la politique de confidentialité.");
    if (!API) return toast.error(`Formulaire indisponible. Appelez-nous au ${restaurant.phone}.`);
    setLoading(true);
    try {
      await axios.post(`${API}/hall-requests`, form);
      toast.success("Merci ! Votre demande a bien été envoyée. Le restaurant vous recontactera.");
      setForm(empty);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "L’envoi a échoué. Vérifiez vos informations ou appelez-nous.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl border border-[#D4A359]/20 bg-[#123322]/80 p-6 sm:p-10" data-testid="hall-form">
      <h2 className="font-display text-3xl sm:text-4xl">Demande de renseignements ou de devis</h2>
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <Field label="Nom *" id="hf-name"><input id="hf-name" required maxLength={100} value={form.name} onChange={set("name")} className="field" data-testid="hall-form-name" /></Field>
        <Field label="Téléphone *" id="hf-phone"><input id="hf-phone" type="tel" required minLength={6} maxLength={30} value={form.phone} onChange={set("phone")} className="field" data-testid="hall-form-phone" /></Field>
        <Field label="E-mail *" id="hf-email"><input id="hf-email" type="email" required maxLength={150} value={form.email} onChange={set("email")} className="field" data-testid="hall-form-email" /></Field>
        <Field label="Type d’événement *" id="hf-type">
          <select id="hf-type" value={form.event_type} onChange={set("event_type")} className="field" data-testid="hall-form-type">
            {eventTypes.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Date souhaitée" id="hf-date"><input id="hf-date" type="date" value={form.event_date} onChange={set("event_date")} className="field" data-testid="hall-form-date" /></Field>
        <Field label="Nombre de personnes (approx.)" id="hf-guests"><input id="hf-guests" inputMode="numeric" maxLength={20} value={form.guests} onChange={set("guests")} className="field" data-testid="hall-form-guests" /></Field>
        <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
        <div className="md:col-span-2">
          <Field label="Votre projet" id="hf-msg"><textarea id="hf-msg" rows={4} maxLength={3000} value={form.message} onChange={set("message")} className="field resize-y" data-testid="hall-form-message" /></Field>
        </div>
      </div>
      <label className="mt-6 flex items-start gap-3 text-sm text-[#D6CFC0] cursor-pointer">
        <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 w-4 h-4 accent-[#D4A359]" data-testid="hall-form-consent" />
        <span>J’accepte que mes données soient utilisées par CAP SUD uniquement pour répondre à ma demande. Voir la <Link to="/politique-de-confidentialite" className="underline text-[#E5B66C]">politique de confidentialité</Link>.</span>
      </label>
      <button type="submit" disabled={loading} className="btn-gold mt-8 disabled:opacity-60" data-testid="hall-form-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer ma demande
      </button>
    </form>
  );
};

export default function HallRental() {
  return (
    <>
      <PageHeader testId="hall-header" eyebrow="Événements" lines={["Location", <em key="s" className="text-[#E5B66C]">de salle</em>]} intro="Location de salle à Morangis : anniversaires, mariages, fêtes de famille ou autres événements, contactez-nous pour connaître les possibilités." />
      <section className="pb-24 sm:pb-32">
        <div className="container-site grid gap-8 lg:grid-cols-12 lg:items-start">
          <Reveal className="lg:col-span-5 space-y-6">
            <PhotoPlaceholder src={restaurant.hallImage} alt="La salle de CAP SUD" label="Photo de la salle" sub="À ajouter par le restaurant" className="aspect-[4/3] rounded-[28px] border border-[#D4A359]/30" testId="hall-photo-slot" />
            <div className="flex flex-wrap gap-2">
              {["Anniversaires", "Mariages", "Fêtes de famille", "Autres événements"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 rounded-full border border-[#D4A359]/30 px-4 py-2 text-sm text-[#F7F4EB]"><PartyPopper className="w-4 h-4 text-[#D4A359]" /> {t}</span>
              ))}
            </div>
            <p className="text-sm text-[#D6CFC0]">Capacité, tarifs et équipements : renseignez-vous directement auprès du restaurant au <a href={restaurant.phoneHref} className="text-[#E5B66C] underline" data-testid="hall-phone-link">{restaurant.phone}</a>.</p>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1}><HallForm /></Reveal>
        </div>
      </section>
    </>
  );
}
